import * as React from 'react';
import { isDefined, noop, saveNodeType, getValueFromOverride } from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { EachFixedRangeVolumeProfile } from './Wrapper';
import { chartOrder } from '@/utils/chartConfig';
import { clampYToChart, ShapeType } from '@/index';
import type { ChartId, DragOverrideBase, DrawingSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';

import { ChartShapesContext } from '@/contexts/ChartShapesContext';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { toVolumeProfileRFCProps, RFC_RENDER_MODE } from '@/utils/shapePropsToAppearance';
import { volumeProfileDefaults } from '@/utils/defaultShapePropertyValues';
import type { FixedRangeVolumeProfileRenderNode } from '@/types/commontypes';

interface FixedRangeVolumeProfileShape {
  uuid: string;
  start: [number, number]; // [x, y] - first click position
  end: [number, number]; // [x, y] - second click position
  selected: boolean;
  locked: boolean;
  chartId: string;
  type: ShapeType;
  hidden: boolean; // New property to control visibility
}

export interface FixedRangeVolumeProfileProps {
  enabled: boolean;
  chartId: string;
  trends: FixedRangeVolumeProfileShape[];
  sharedState?: DrawingSharedState;
  setSharedState?: (state: DrawingSharedState) => void;
  onStart?: (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => void;
  onComplete?: (e: React.MouseEvent, trends: FixedRangeVolumeProfileShape[], moreProps: MoreProps) => void;
  onSelect?: (e: React.MouseEvent, trends: FixedRangeVolumeProfileShape[], moreProps: MoreProps) => void;
  onDragComplete?: (e: React.MouseEvent, trends: FixedRangeVolumeProfileShape[], moreProps: MoreProps) => void;
  onHover?: (hoveredId: string | null, moreProps: MoreProps) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  hoverId?: string;
  snap?: boolean;
  snapTo?: (value: number) => number;
  shouldDisableSnap?: (e: React.MouseEvent) => boolean;
  currentPositionStroke?: string;
  currentPositionStrokeWidth?: number;
  currentPositionRadius?: number;
  currentPositionstrokeOpacity?: number;
  hoverText?: Partial<HoverTextConfig>;
  isAnyShapeEnabled?: boolean;
}

interface FixedRangeVolumeProfileOverride extends DragOverrideBase {
  x1Value: number;
  x2Value: number;
  y1Value: number;
  y2Value: number;
}

interface FixedRangeVolumeProfileState {
  override?: FixedRangeVolumeProfileOverride;
  trends?: FixedRangeVolumeProfileShape[];
}

type Props = FixedRangeVolumeProfileProps;
type State = FixedRangeVolumeProfileState;

export class FixedRangeVolumeProfile extends React.Component<Props, State> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: Record<string, any> | null = null;

  static get defaultProps() {
    if (!FixedRangeVolumeProfile._cachedDefaultProps) {
      FixedRangeVolumeProfile._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#1e53e6',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 4,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select volume profile',
          selectedText: '',
        },
        trends: [],
      };
    }
    return FixedRangeVolumeProfile._cachedDefaultProps;
  }

  private saveNodeType: <T>(idx: number) => React.Ref<T>;

  public constructor(props: FixedRangeVolumeProfileProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    this.state = {};
  }

  public render() {
    const {
      currentPositionstrokeOpacity,
      currentPositionRadius = FixedRangeVolumeProfile.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      enabled,
      hoverText,
      shouldDisableSnap,
      snap,
      snapTo,
      trends,
      onDoubleClick,
      sharedState,
      chartId,
      isAnyShapeEnabled,
    } = this.props;
    const { override } = this.state;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(ShapeType.FIXED_RANGE_VOLUME_PROFILE) as FixedRangeVolumeProfileRenderNode;
    const baseAppearance = {
      ...toVolumeProfileRFCProps(activeNode),
      volumeProfile: true,
    };
    // Check if we're currently drawing
    const tempCondition =
      sharedState?.type === ShapeType.FIXED_RANGE_VOLUME_PROFILE &&
      chartId === sharedState.chartId &&
      sharedState.endXY &&
      sharedState.status !== 'completed';

    const tempLine = tempCondition ? (
      <EachFixedRangeVolumeProfile
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        type={RFC_RENDER_MODE[ShapeType.FIXED_RANGE_VOLUME_PROFILE] as 'XLINE' | 'RAY' | 'LINE'}
        uuid={''}
        isTemp={true}
        x1Value={sharedState?.startXY?.[0] ?? 0}
        y1Value={sharedState?.startXY?.[1] ?? 0}
        x2Value={sharedState?.endXY?.[0] ?? 0}
        y2Value={sharedState?.endXY?.[1] ?? 0}
        appearance={baseAppearance}
        selected={true}
        locked={false}
        onDrag={this.handleDragLine}
        onDragComplete={this.handleDragLineComplete}
        edgeInteractiveCursor="big-declined-arrow-cursor"
        lineInteractiveCursor="react-financial-charts-pointer-cursor"
        onDoubleClick={onDoubleClick}
        strokeOpacity={1}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        strokeStyle={((baseAppearance as Record<string, unknown>).strokeStyle as string) || '#000000'}
        extendLeft={false}
        extendRight={false}
        arrowDirection="none"
        sharedState={sharedState}
        isAnyShapeEnabled={isAnyShapeEnabled}
      />
    ) : null;

    return (
      <g>
        {trends.map((each, idx) => {
          if (each.hidden) return null;
          // 좌표가 아직 복원되지 않은 경우 (candleData 로드 전) 렌더링 건너뛰기
          if (!each.start || !each.end) return null;

          const eachAppearance = {
            ...toVolumeProfileRFCProps(each as unknown as FixedRangeVolumeProfileRenderNode),
            volumeProfile: true,
          };

          const hoverTextWithDefault = {
            ...FixedRangeVolumeProfile.defaultProps.hoverText,
            ...hoverText,
          };

          return (
            <EachFixedRangeVolumeProfile
              key={idx}
              uuid={each.uuid}
              ref={this.saveNodeType(idx)}
              index={idx}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              type={RFC_RENDER_MODE[ShapeType.FIXED_RANGE_VOLUME_PROFILE] as 'XLINE' | 'RAY' | 'LINE'}
              selected={(each as unknown as { selected?: boolean }).selected}
              x1Value={getValueFromOverride(override, each.uuid, 'x1Value', each.start[0])}
              y1Value={getValueFromOverride(override, each.uuid, 'y1Value', each.start[1])}
              x2Value={getValueFromOverride(override, each.uuid, 'x2Value', each.end[0])}
              y2Value={getValueFromOverride(override, each.uuid, 'y2Value', each.end[1])}
              appearance={eachAppearance}
              hoverText={hoverTextWithDefault}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              edgeInteractiveCursor="big-declined-arrow-cursor"
              lineInteractiveCursor="react-financial-charts-move-cursor"
              onSelect={this.handleSelect}
              onHover={(uuid: string | null, moreProps?: MoreProps) => this.handleHover(uuid, moreProps!)}
              locked={each.locked}
              onDoubleClick={onDoubleClick}
              strokeOpacity={1}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              strokeStyle={((eachAppearance as Record<string, unknown>).strokeStyle as string) || '#000000'}
              extendLeft={false}
              extendRight={false}
              arrowDirection="none"
              sharedState={sharedState ?? {}}
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled ?? false}
          snap={snap ?? false}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={(e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            this.handleStart(e, xyValue as [number, number], moreProps)
          }
          onMouseMove={(_: React.MouseEvent, xyValue: number[], moreProps: MoreProps) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            this.handleDrawLine(_, xyValue as [number, number], moreProps)
          }
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | null, moreProps: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { trends } = this.props;
    const newTrends = trends.map(trend => ({
      ...trend,
      selected: trend.uuid === selectedUuid,
    }));

    const { onSelect } = this.props;
    if (onSelect !== undefined) {
      onSelect(e, newTrends, moreProps);
    }
  };

  private readonly handleStart = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { sharedState, enabled } = this.props;
    if (!enabled) return;

    // Clamp X to last candle position (prevent selecting beyond chart data)
    const clampedX = this.clampXToLastCandle(xyValue[0], moreProps);

    if (sharedState?.status !== 'started') {
      const { onStart } = this.props;
      if (onStart !== undefined) {
        onStart(e, [clampedX, xyValue[1]], moreProps);
      }
    } else {
      if (e.detail !== 1) return;
      const shapesChartId = sharedState?.chartId;
      // Clamp Y value to chart boundaries
      const yValue = clampYToChart(moreProps.chartId, shapesChartId, moreProps.chartConfigs, chartOrder, xyValue[1]);
      this.handleEnd(e, [clampedX, yValue], moreProps);
    }
  };

  private readonly handleEnd = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { trends: _trends, sharedState, enabled } = this.props;
    const chartId = sharedState?.chartId ?? moreProps.chartId;
    if (!enabled || !chartId) return;

    if (sharedState?.status === 'started' && sharedState?.startXY) {
      const startXY = sharedState.startXY;
      const startX = startXY[0];
      const endX = xyValue[0];

      // Shapes created via handleEnd use default storage-shape fields; RFC props
      // are derived by the adapter at render time.
      const lastProps = getActiveTemplateNode(ShapeType.FIXED_RANGE_VOLUME_PROFILE);
      const initial = { ...volumeProfileDefaults, ...lastProps };

      this.commitRangeUpdate(e, moreProps, {
        mode: 'create',
        x1Value: startX,
        x2Value: endX,
        fallbackTop: startXY[1],
        fallbackBottom: xyValue[1],
        createPayload: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          initialProps: initial as unknown as Record<string, unknown>,
          chartId,
          locked: false,
        },
      });
    }
  };

  private readonly handleDrawLine = (_: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { setSharedState, sharedState, chartId, enabled } = this.props;
    if (!sharedState || sharedState.status !== 'started' || !enabled) return;

    const startChartId = sharedState.chartId;
    // Only draw for shapes created on this chart
    if (chartId !== startChartId) return;

    let [x] = xyValue;
    const [, y] = xyValue;

    // Clamp X to last candle position (prevent selecting beyond chart data)
    x = this.clampXToLastCandle(x, moreProps);

    setSharedState?.({
      ...sharedState,
      endXY: [x, y],
    });
  };

  private readonly handleDragLineComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { override } = this.state;
    if (!isDefined(override)) return;

    this.commitRangeUpdate(e, moreProps, {
      mode: 'update',
      id: override.uuid,
      x1Value: override.x1Value,
      x2Value: override.x2Value,
      fallbackTop: override.y1Value,
      fallbackBottom: override.y2Value,
      opts: { clearOverride: true },
    });
  };

  private readonly handleDragLine = (
    _: React.MouseEvent,
    uuid: string | undefined,
    newXYValue: Record<string, unknown>,
  ) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (uuid === undefined) return;
    if (override && override.uuid !== uuid) return;

    // Apply defense logic only when hoverId exists (allows cases where hoverId becomes null during dragging)
    if (hoverId && hoverId !== uuid) return;

    // Clamp X to last candle position (prevent dragging beyond chart data)
    const { lastCandleX, x1Value: rawX1, x2Value: rawX2, y1Value, y2Value } = newXYValue;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    let x1Value = rawX1 as number;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    let x2Value = rawX2 as number;
    if (lastCandleX !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      x1Value = Math.min(x1Value, lastCandleX as number);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      x2Value = Math.min(x2Value, lastCandleX as number);
    }

    this.setState({
      override: {
        uuid,
        x1Value,
        x2Value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        y1Value: y1Value as number,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        y2Value: y2Value as number,
      },
    });
  };

  /**
   * Clamp X coordinate to last candle position.
   * Prevents selecting beyond chart data range.
   */
  private clampXToLastCandle(x: number, moreProps: MoreProps): number {
    const { fullData, xAccessor } = moreProps;
    if (!fullData || fullData.length === 0) return x;

    const lastCandle = fullData[fullData.length - 1];
    const lastCandleX = xAccessor(lastCandle);

    // If mouse X is beyond last candle, clamp to last candle X
    if (x > lastCandleX) {
      return lastCandleX;
    }
    return x;
  }

  private computeBoxTopBottomFromXRange(
    fullData: Record<string, unknown>[],
    xAccessor: (d: Record<string, unknown>) => number,
    x1Value: number,
    x2Value: number,
    fallbackTop: number,
    fallbackBottom: number,
  ) {
    const minX = Math.min(x1Value, x2Value);
    const maxX = Math.max(x1Value, x2Value);

    const startIndex = fullData?.findIndex((d: Record<string, unknown>) => xAccessor(d) >= minX) ?? -1;
    const endIndex = fullData?.findIndex((d: Record<string, unknown>) => xAccessor(d) >= maxX) ?? -1;

    let boxTop = fallbackTop;
    let boxBottom = fallbackBottom;

    if (fullData && startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
      const candles = fullData.slice(startIndex, endIndex + 1);

      const highs = candles
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        .map((d: Record<string, unknown>) => (d.ohlcv as number[])?.[2])
        .filter((v: number | undefined) => v != null && !isNaN(v));

      const lows = candles
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        .map((d: Record<string, unknown>) => (d.ohlcv as number[])?.[3])
        .filter((v: number | undefined) => v != null && !isNaN(v));

      if (highs.length && lows.length) {
        boxTop = Math.max(...highs);
        boxBottom = Math.min(...lows);
      }
    }

    return { boxTop, boxBottom };
  }

  /**
   * Check if at least 2 candles are selected in the given X range.
   * Returns true if valid (2+ candles), false otherwise.
   */
  private isValidCandleRange(
    fullData: Record<string, unknown>[] | undefined,
    xAccessor: (d: Record<string, unknown>) => number,
    x1Value: number,
    x2Value: number,
  ): boolean {
    if (!fullData || fullData.length === 0) return false;

    const minX = Math.min(x1Value, x2Value);
    const maxX = Math.max(x1Value, x2Value);

    const startIndex = fullData.findIndex((d: Record<string, unknown>) => xAccessor(d) >= minX);
    const endIndex = fullData.findIndex((d: Record<string, unknown>) => xAccessor(d) >= maxX);

    if (startIndex === -1 || endIndex === -1) return false;

    // Need at least 2 candles (startIndex !== endIndex)
    return startIndex !== endIndex;
  }

  private commitRangeUpdate(
    e: React.MouseEvent,
    moreProps: MoreProps,
    params: {
      // Common
      mode: 'create' | 'update';
      x1Value: number;
      x2Value: number;
      fallbackTop: number;
      fallbackBottom: number;

      // update only
      id?: string;

      // create only (required in handleEnd)
      createPayload?: {
        /** storage-shape 필드 (template node에서 spread) — `border`, `levels` 등 도형 속성. */
        initialProps: Record<string, unknown>;
        chartId: ChartId;
        locked?: boolean;
      };

      //Callback/Status Options
      opts?: { clearOverride?: boolean };
    },
  ) {
    const { fullData, xAccessor } = moreProps;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const typedFullData = fullData as unknown as Record<string, unknown>[] | undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const typedXAccessor = xAccessor as unknown as (d: Record<string, unknown>) => number;

    // Validate: at least 2 candles must be selected
    const isValid = this.isValidCandleRange(typedFullData, typedXAccessor, params.x1Value, params.x2Value);

    if (!isValid) {
      // For create mode: cancel creation and reset shared state
      if (params.mode === 'create') {
        const { setSharedState } = this.props;
        setSharedState?.({
          status: 'idle',
          type: undefined,
          chartId: undefined,
          startXY: undefined,
          endXY: undefined,
        });
        return;
      }

      // For update mode: revert to previous state (clear override without applying changes)
      if (params.opts?.clearOverride) {
        this.setState({ override: undefined });
      }
      return;
    }

    const { boxTop, boxBottom } = this.computeBoxTopBottomFromXRange(
      typedFullData ?? [],
      typedXAccessor,
      params.x1Value,
      params.x2Value,
      params.fallbackTop,
      params.fallbackBottom,
    );

    // ✅ 1) create
    if (params.mode === 'create') {
      const { trends } = this.props;
      const p = params.createPayload!;
      const shapeUuid = crypto.randomUUID();
      const now = Date.now();
      const newTrend = {
        ...p.initialProps,
        start: [params.x1Value, boxTop] as [number, number],
        end: [params.x2Value, boxBottom] as [number, number],
        selected: true,
        type: ShapeType.FIXED_RANGE_VOLUME_PROFILE,
        uuid: shapeUuid,
        chartId: p.chartId,
        locked: p.locked ?? false,
        hidden: false,
        createdAt: now,
        updatedAt: now,
      };

      const newTrends = [...trends.map(d => ({ ...d, selected: false })), newTrend];

      this.setState({ trends: newTrends }, () => {
        const { onComplete } = this.props;
        onComplete?.(e, newTrends, moreProps);
      });

      return;
    }

    // ✅ 2) update (drag complete)
    const { trends, onDragComplete } = this.props;
    const targetId = params.id!;
    const newTrends: FixedRangeVolumeProfileShape[] = trends.map(each =>
      each.uuid === targetId
        ? {
            ...each,
            start: [params.x1Value, boxTop] as [number, number],
            end: [params.x2Value, boxBottom] as [number, number],
            selected: true,
          }
        : { ...each, selected: false },
    );

    onDragComplete?.(e, newTrends, moreProps);
    if (params.opts?.clearOverride) {
      this.setState({ override: undefined });
    }
  }
}
